'use client';

import styled from 'styled-components';
import { Typography } from '@/components/ui-components/Typography';
import { Skeleton } from '@/components/skeletons';

interface MetricCardProps {
  title: string;
  value: string | number;
  unit?: string;
  change?: {
    value: string | number;
    trend: 'up' | 'down' | 'neutral';
    unit?: string;
  };
  variant?: 'default' | 'simple';
  loading?: boolean;
  className?: string;
}

const CardContainer = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 15px 15px 15px 24px;
  box-shadow: 0 1px 4px rgba(2, 6, 23, 0.06);
  border: 1px solid #e5e7eb;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  min-height: 112px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &:hover {
    box-shadow: 0 4px 14px rgba(2, 6, 23, 0.08);
    transform: translateY(-1px);
  }
`;

const TitleWrapper = styled.div`
  margin-bottom: 12px;
`;

const Row = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

const ValueContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const ChangeContainer = styled.div<{ $trend: 'up' | 'down' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: 'Plus Jakarta Sans', 'Open Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 21px;
  letter-spacing: 0px;
  text-align: right;
  color: ${({ $trend }) =>
    $trend === 'up' ? '#14AE5C' : $trend === 'down' ? '#EC221F' : '#6b7280'};
`;

const TrendIcon = styled.span<{ $trend: 'up' | 'down' | 'neutral' }>`
  font-size: 12px;
  line-height: 1;
`;

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  unit,
  change,
  variant = 'default',
  loading = false,
  className
}) => {
  if (loading) {
    return (
      <CardContainer className={className}>
        <TitleWrapper>
          <Skeleton width="60%" height="14px" />
        </TitleWrapper>
        <Row>
          <ValueContainer>
            <Skeleton width="80px" height="32px" />
          </ValueContainer>
          {variant === 'default' && (
            <Skeleton width="60px" height="20px" />
          )}
        </Row>
      </CardContainer>
    );
  }
  const renderChangeValue = () => {
    if (!change || variant === 'simple') return null;
    
    const prefix = change.trend === 'up' ? '+' : change.trend === 'down' ? '-' : '';
    const icon = change.trend === 'up' ? '↑' : change.trend === 'down' ? '↓' : '';
    
    return (
      <ChangeContainer $trend={change.trend}>
        {prefix}{change.value}{change.unit && ` ${change.unit}`}
        <TrendIcon $trend={change.trend}>{icon}</TrendIcon>
      </ChangeContainer>
    );
  };

  const renderContent = () => {
    if (variant === 'simple') {
      return (
        <ValueContainer>
          <Typography variant="metric-value">{value}</Typography>
          {unit && <Typography variant="metric-value">{unit}</Typography>}
        </ValueContainer>
      );
    }

    return (
      <Row>
        <ValueContainer>
          <Typography variant="metric-value">{value}</Typography>
          {unit && <Typography variant="metric-value">{unit}</Typography>}
        </ValueContainer>
        {renderChangeValue()}
      </Row>
    );
  };

  return (
    <CardContainer className={className}>
      <TitleWrapper>
        <Typography variant="label-uppercase">{title}</Typography>
      </TitleWrapper>
      {renderContent()}
    </CardContainer>
  );
};
